import { X, MessageSquarePlus } from "lucide-react";

const CONTACT_EMAIL = "support@whatsyourbalance.com";

const STEPS = [
  {
    title: "Enter today's bank balance",
    body: "On the Dashboard, open the top card and set each Bank-Cash account to whatever your bank actually shows right now. This anchors every forecast.",
  },
  {
    title: "Reconcile pending charges",
    body: "If a bill was already paid but hasn't cleared your bank, mark it Pending — the app deducts it from your balance so the forecast stays honest.",
  },
  {
    title: "Confirm this pay period's bills",
    body: "In the Current Pay Period card, check the bills due before your next paycheck. Mark them Paid as they clear.",
  },
  {
    title: "Add or edit income",
    body: "Manage Income Sources lets you add paychecks (weekly, biweekly, monthly) or one-time deposits. Recurring paychecks auto-populate future pay periods.",
  },
  {
    title: "See what's ahead",
    body: "Future Pay Periods shows the next paychecks with their assigned bills. Monthly Outlook shows the full-month net for the current month and every month ahead.",
  },
];

const TIPS = [
  "Balances are manual — the app doesn't connect to your bank. Update them any time your real balance changes.",
  "Recurring bill edits offer a scope prompt: just this occurrence vs. this and all future.",
  "Bills paid but not yet cleared stay in the Pending band on your Dashboard until you mark them Cleared.",
  "Not sure what to try? Head to Settings → Load demo data for a full sample account.",
];

export function HelpModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-[#2C302E]/40 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="help-modal-backdrop"
    >
      <div
        className="w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="help-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-1">Help</div>
            <div className="font-heading font-bold text-2xl text-[#2C302E]">Getting started</div>
            <p className="text-sm text-[#59605C] mt-1">
              A calm 5-step rhythm that keeps your forecast honest.
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost !p-2"
            aria-label="Close help"
            data-testid="help-close-button"
          >
            <X size={18} />
          </button>
        </div>

        <ol className="space-y-4" data-testid="help-steps-list">
          {STEPS.map((s, i) => (
            <li
              key={i}
              className="flex gap-3 rounded-2xl border border-[#D8CFB9] bg-[#F9F8F6] p-4"
              data-testid={`help-step-${i}`}
            >
              <span className="shrink-0 w-8 h-8 rounded-full bg-[#4A6148] text-white text-sm font-heading font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <div>
                <div className="font-heading font-semibold text-[#2C302E]">{s.title}</div>
                <p className="text-sm text-[#59605C] mt-1 leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-6 rounded-2xl border border-[#D8CFB9] bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-2">Tips</div>
          <ul className="space-y-2 text-sm text-[#59605C] leading-relaxed" data-testid="help-tips-list">
            {TIPS.map((t, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-[#4A6148]">·</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 rounded-2xl border border-[#D8CFB9] bg-[#EEF3EC] p-4" data-testid="help-contact-block">
          <div className="font-heading font-semibold text-[#2C302E]">Still stuck? Reach out.</div>
          <p className="text-sm text-[#59605C] mt-1">
            Email
            {" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-[#4A6148] underline"
              data-testid="help-contact-email"
            >
              {CONTACT_EMAIL}
            </a>
            {" "}or use the floating <span className="inline-flex items-center gap-1 font-medium"><MessageSquarePlus size={13} /> Send feedback</span> button at the bottom-right of any page.
          </p>
        </div>
      </div>
    </div>
  );
}
